import { formatDateUS } from '../../../utils/commonFn';
import type { RefBadgeTone } from '../../candidate-pipeline/reference/ReferenceTableParts';
import { AiReferencePerson, AiReferenceSettingsRow } from './gql';

// GraphQL returns enum KEYS. Labels + badge tones are identical to the
// candidate References tab (AiReferenceSection) so a status reads the same on
// both pages: in-progress → info, waiting on a human → warn, failed → danger,
// closed → neutral (design handoff 2a).
export const AI_REFERENCE_STATUS_META: Record<string, { label: string; tone: RefBadgeTone }> = {
  NEW: { label: 'Not Sent', tone: 'neutral' },
  SEND_OUTREACH: { label: 'Queued', tone: 'info' },
  IN_QUEUE: { label: 'Queued', tone: 'info' },
  OUTREACH_SENT: { label: 'Outreach Sent', tone: 'info' },
  AWAITING_RESPONSE: { label: 'Awaiting Response', tone: 'info' },
  SCHEDULED: { label: 'Call Scheduled', tone: 'info' },
  CALL_COMPLETED: { label: 'Processing Call', tone: 'info' },
  COMPLETED: { label: 'Completed', tone: 'active' },
  NEEDS_HUMAN_OUTREACH: { label: 'Needs Human', tone: 'warn' },
  DECLINED: { label: 'Declined', tone: 'danger' },
  CLOSED: { label: 'Closed', tone: 'neutral' },
};

/**
 * One filter option per distinct label. Statuses that share a label ("Queued"
 * = SEND_OUTREACH + IN_QUEUE) collapse into one option whose `value` is the
 * comma-joined enum keys — split it with `statusOptionKeys` before sending.
 */
export const AI_REFERENCE_STATUS_OPTIONS: { value: string; label: string }[] = Object.entries(
  AI_REFERENCE_STATUS_META
).reduce<{ value: string; label: string }[]>((acc, [key, meta]) => {
  // Closed only means "contact deleted" now and is never listed — no filter option.
  if (key === 'CLOSED') {
    return acc;
  }
  const existing = acc.find((o) => o.label === meta.label);
  if (existing) {
    existing.value = `${existing.value},${key}`;
  } else {
    acc.push({ value: key, label: meta.label });
  }
  return acc;
}, []);

export const statusOptionKeys = (value?: string | null): string[] =>
  (value ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

export const NEEDS_HUMAN_REASON_LABEL: Record<string, string> = {
  NO_EMAIL: 'No email',
  SEND_FAILED: 'Email failed to send',
  ACTIVE_DECLINE: 'Contact asked for a human',
  NO_RESPONSE: 'No response',
  UNREACHABLE_MISSED_AGAIN: 'Missed the call again',
  SILENT_CALL: 'Silent call',
};

export const DECLINE_REASON_LABEL: Record<string, string> = {
  COMPANY_POLICY: 'Company policy',
  NOT_FAMILIAR_ENOUGH: 'Not familiar enough',
  PREFER_NOT_TO_PARTICIPATE: 'Prefers not to participate',
};


// Statuses that have extracted answers worth opening.
export const ANSWERABLE_STATUSES = ['COMPLETED', 'CLOSED'];

export const statusLabel = (status?: string | null): string =>
  status ? AI_REFERENCE_STATUS_META[status]?.label ?? status : '--';

export const statusDetail = (
  row: Pick<AiReferenceSettingsRow, 'status' | 'needsHumanReason' | 'declineReason'>
): string | null => {
  if (row?.status === 'NEEDS_HUMAN_OUTREACH' && row?.needsHumanReason) {
    return NEEDS_HUMAN_REASON_LABEL[row.needsHumanReason] ?? row.needsHumanReason;
  }
  if (row?.status === 'DECLINED' && row?.declineReason) {
    return DECLINE_REASON_LABEL[row.declineReason] ?? row.declineReason;
  }
  return null;
};

export const personName = (person?: AiReferencePerson | null): string => {
  const name = [person?.firstName, person?.lastName]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return name || '--';
};

export const formatDate = (value?: string | null): string => {
  if (!value || Number.isNaN(new Date(value).getTime())) {
    return '--';
  }
  return formatDateUS(value) || '--';
};

export const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '--';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }
  return `${formatDateUS(value)} ${date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
};

/** Most recent of outreach / completion / close / entry update. */
export const lastActivity = (row: AiReferenceSettingsRow): string | null => {
  const candidates = [row?.updatedAt, row?.lastOutreachAt, row?.completedAt, row?.closedAt]
    .filter((v): v is string => !!v)
    .map((v) => ({ v, t: new Date(v).getTime() }))
    .filter(({ t }) => !Number.isNaN(t))
    .sort((a, b) => b.t - a.t);
  return candidates[0]?.v ?? null;
};
